// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React, { useMemo } from 'react';
import { Box, Typography, Chip, Avatar, Divider } from '@mui/material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';

interface DealStatusProps {
  dealInfo: any;
  onStagePress?: () => void;
}

const DealStatus: React.FC<DealStatusProps> = ({ dealInfo, onStagePress }) => {
  const { theme } = useTheme();

  const dealTags = useMemo(() => {
    return (
      dealInfo?.tags?.items?.map((tagAssignment: any) => ({
        id: tagAssignment.tag?.id,
        name: tagAssignment.tag?.name,
        color: tagAssignment.tag?.color,
        icon: tagAssignment.tag?.icon,
      })) || []
    );
  }, [dealInfo?.tags]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!dealInfo) {
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          backgroundColor: theme.background2,
          borderRadius: 2,
          p: 2,
        }}
      >
        {/* Stage */}
        {dealInfo?.stage?.title && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                cursor: onStagePress ? 'pointer' : 'default',
              }}
              onClick={onStagePress}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: theme.text }}
              >
                Stage
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={dealInfo.stage.title}
                  size="small"
                  sx={{
                    backgroundColor: theme.primaryLight,
                    color: theme.primary,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                />
              </Box>
            </Box>
            <Divider sx={{ borderColor: theme.stroke }} />
          </>
        )}

        {/* Last Visit */}
        {dealInfo?.lastVisited && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
              }}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: theme.text }}
              >
                Last Visit
              </Typography>
              <Typography
                sx={{ fontSize: 14, fontWeight: 300, color: theme.text }}
              >
                {formatDate(dealInfo.lastVisited)}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: theme.stroke }} />
          </>
        )}

        {/* Last Visited By */}
        {dealInfo?.lastVisitedByName && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
              }}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: theme.text }}
              >
                Visited By
              </Typography>
              <Typography
                sx={{ fontSize: 14, fontWeight: 300, color: theme.text }}
              >
                {dealInfo.lastVisitedByName}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: theme.stroke }} />
          </>
        )}

        {/* Disposition */}
        {dealInfo?.disposition?.title &&
          dealInfo.disposition.title !== 'No Disposition' &&
          dealInfo.disposition.title !== 'Multi-Unit' && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: theme.text }}
                >
                  Disposition
                </Typography>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 300, color: theme.text }}
                >
                  {dealInfo.disposition.title}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: theme.stroke }} />
            </>
          )}

        {/* Setter */}
        {dealInfo?.setter && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
              }}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: theme.text }}
              >
                Setter
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {dealInfo.setter?.profilePicture ? (
                  <Avatar
                    src={dealInfo.setter.profilePicture}
                    sx={{ width: 24, height: 24 }}
                  />
                ) : (
                  <Avatar sx={{ width: 24, height: 24, fontSize: 11 }}>
                    {dealInfo.setter?.user?.initials || '?'}
                  </Avatar>
                )}
                <Typography
                  sx={{ fontSize: 14, fontWeight: 300, color: theme.text }}
                >
                  {dealInfo.setter?.user?.name || 'Unknown'}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ borderColor: theme.stroke }} />
          </>
        )}

        {/* Closer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.text }}>
            Closer
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dealInfo?.closer ? (
              <>
                {dealInfo.closer?.profilePicture ? (
                  <Avatar
                    src={dealInfo.closer.profilePicture}
                    sx={{ width: 24, height: 24 }}
                  />
                ) : (
                  <Avatar sx={{ width: 24, height: 24, fontSize: 11 }}>
                    {dealInfo.closer?.user?.initials || '?'}
                  </Avatar>
                )}
                <Typography
                  sx={{ fontSize: 14, fontWeight: 300, color: theme.text }}
                >
                  {dealInfo.closer?.user?.name || 'Unknown'}
                </Typography>
              </>
            ) : (
              <Typography
                sx={{ fontSize: 14, fontWeight: 300, color: theme.textLight }}
              >
                Unassigned
              </Typography>
            )}
          </Box>
        </Box>

        {/* Tags */}
        {dealTags.length > 0 && (
          <>
            <Divider sx={{ borderColor: theme.stroke }} />
            <Box sx={{ py: 1 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: theme.text,
                  mb: 1,
                }}
              >
                Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {dealTags.map((tag: any) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    sx={{
                      backgroundColor: tag.color
                        ? `${tag.color}20`
                        : theme.primaryLight,
                      color: tag.color || theme.primary,
                      fontWeight: 500,
                      fontSize: 12,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DealStatus;
